
import { defineComponent, PropType } from 'vue';
import {
    TreeItem,
    SelectionType,
    ChildrenLoadState,
    TreeItemEventType,
    TreeSelectionEvent,
    TreeActivateEvent,
    TreeExpandEvent,
    TreeFocusEvent,
    TreeItemNode,
} from './tree-item';

export default defineComponent({
    name: 'psv-tree-node',
    emits: [
        'toggle-select-item',
        'toggle-expand-item',
        'activate-item',
        'focus-item',
    ],
    props: {
        items: {
            type: Array as PropType<Array<TreeItem<unknown>>>,
            required: true,
        },
        parents: {
            type: Array as PropType<Array<TreeItemNode<unknown>>>,
            required: true,
        },
        populateChildren: {
            type: Function as PropType<
                (
                    node: TreeItem<unknown>,
                    parents: Array<TreeItem<unknown>>
                ) => Promise<Array<TreeItem<unknown>>>
            >,
        },
    },
    methods: {
        childrenLoaded(item: TreeItem<unknown>) {
            return (
                !item.isLeaf &&
                item.childrenLoadState === ChildrenLoadState.Loaded
            );
        },
        childrenLoading(item: TreeItem<unknown>) {
            return (
                !item.isLeaf &&
                item.childrenLoadState === ChildrenLoadState.Loading
            );
        },
        ariaExpandedState(item: TreeItem<unknown>) {
            if (item.isLeaf) {
                return undefined;
            }
            return item.expanded;
        },
        itemParents(item: TreeItem<unknown>): Array<TreeItem<unknown>> {
            return [...this.parents, item];
        },
        focusItem(item: TreeItem<unknown>) {
            const focusEvent: TreeFocusEvent<unknown> = {
                item: item,
                type: TreeItemEventType.Focus,
                parents: this.parents,
            };
            this.$emit('focus-item', focusEvent);
        },
        selectItemInternal(item: TreeItem<unknown>, selectType: SelectionType) {
            const event: TreeSelectionEvent<unknown> = {
                item,
                type: TreeItemEventType.Selection,
                parents: this.parents,
                selectType,
                selected: !item.selected,
            };
            this.$emit('toggle-select-item', event);
            if (item.selected && !item.expanded) {
                this.toggleExpand(item);
            }

            if (item.selected) {
                const focusEvent: TreeFocusEvent<unknown> = {
                    item,
                    type: TreeItemEventType.Focus,
                    parents: this.parents,
                };
                console.log('Emitting focus event', focusEvent);
                this.$emit('focus-item', focusEvent);
            }
        },
        selectItem(item: TreeItem<unknown>) {
            this.selectItemInternal(item, SelectionType.Replace);
        },
        appendSelection(item: TreeItem<unknown>) {
            this.selectItemInternal(item, SelectionType.Append);
        },
        extendSelection(item: TreeItem<unknown>) {
            this.selectItemInternal(item, SelectionType.Extend);
        },
        activateItem(item: TreeItem<unknown>, evt: MouseEvent) {
            const event: TreeActivateEvent<unknown> = {
                item,
                parents: this.parents,
                type: TreeItemEventType.Activate,
                shiftKey: evt.shiftKey,
                ctrlKey: evt.ctrlKey,
                altKey: evt.altKey,
            };
            this.$emit('activate-item', event);
            this.selectItem(item);
        },
        toggleExpand(item: TreeItem<unknown>) {
            const expandEvent: TreeExpandEvent<unknown> = {
                item,
                parents: this.parents,
                type: TreeItemEventType.Expand,
                expanded: !item.expanded,
            };
            this.$emit('toggle-expand-item', expandEvent);
            if (
                !item.isLeaf &&
                item.childrenLoadState === ChildrenLoadState.Unloaded &&
                this.populateChildren
            ) {
                item.childrenLoadState = ChildrenLoadState.Loading;
                this.populateChildren(item, this.itemParents(item)).then(
                    (children) => {
                        item.childrenLoadState = ChildrenLoadState.Loaded;
                        item.children = children;
                        this.$forceUpdate();
                    }
                );
            }
        },
    },
});
